import { useState, useMemo } from 'react';
import { Badge } from './ui/badge';
import { Button } from './ui/button';
import { Checkbox } from './ui/checkbox';
import { Popover, PopoverContent, PopoverTrigger } from './ui/popover';
import { Building, CalendarClock, CheckSquare, Clock, Link as LinkIcon, MessageSquare, Paperclip, ChevronLeft, ChevronRight, ChevronDown } from 'lucide-react';
import { RT_STATUSES, RT_TYPES, RT_TYPE_MAP } from '../lib/restaurantConstants';
import { getDaysInProgress, formatShortDate } from '../lib/format';

export function RestaurantTaskFilters({
  filterType, setFilterType,
  filterAssignee, setFilterAssignee,
  selectedRestaurants, setSelectedRestaurants,
  users = [], restaurants = [], showRestaurantFilter = false,
  count = 0, onNewTask,
}) {
  const toggleRestaurant = (id) => {
    setSelectedRestaurants(prev => prev.includes(id) ? prev.filter(x => x !== id) : [...prev, id]);
  };

  return (
    <div className="flex flex-col sm:flex-row sm:flex-wrap items-stretch sm:items-center gap-2 sm:gap-3 bg-white border border-[#E0E0E0] rounded-lg p-3">
      <select
        value={filterType}
        onChange={(e) => setFilterType(e.target.value)}
        className="px-3 py-1.5 border border-[#E0E0E0] rounded-md text-sm bg-white"
        data-testid="rt-filter-type"
      >
        <option value="">All Types</option>
        {RT_TYPES.map(t => <option key={t.value} value={t.value}>{t.label}</option>)}
      </select>
      <select
        value={filterAssignee}
        onChange={(e) => setFilterAssignee(e.target.value)}
        className="px-3 py-1.5 border border-[#E0E0E0] rounded-md text-sm bg-white"
        data-testid="rt-filter-assignee"
      >
        <option value="">All Assignees</option>
        {users.map(u => <option key={u.id} value={u.id}>{u.name}</option>)}
      </select>
      {showRestaurantFilter && (
        <Popover>
          <PopoverTrigger asChild>
            <Button variant="outline" size="sm" className="justify-between min-w-[180px]" data-testid="rt-filter-restaurants">
              {selectedRestaurants.length === 0 ? 'All Restaurants' : `${selectedRestaurants.length} restaurant(s)`}
              <ChevronDown size={14} className="ml-2" />
            </Button>
          </PopoverTrigger>
          <PopoverContent className="w-64 p-2 max-h-[300px] overflow-y-auto" align="start">
            {selectedRestaurants.length > 0 && (
              <button
                onClick={() => setSelectedRestaurants([])}
                className="text-xs text-[#FF6B35] hover:underline mb-2 px-1"
                data-testid="rt-filter-restaurants-clear"
              >
                Clear selection
              </button>
            )}
            <div className="space-y-1">
              {restaurants.map(r => (
                <label key={r.id} className="flex items-center gap-2 px-1 py-1 rounded hover:bg-[#F5F5F5] cursor-pointer" data-testid={`rt-filter-restaurant-${r.id}`}>
                  <Checkbox checked={selectedRestaurants.includes(r.id)} onCheckedChange={() => toggleRestaurant(r.id)} />
                  <span className="text-sm truncate">{r.name}</span>
                </label>
              ))}
              {restaurants.length === 0 && <p className="text-xs text-[#999999] px-1">No restaurants</p>}
            </div>
          </PopoverContent>
        </Popover>
      )}
      <span className="text-sm text-[#666666]">{count} tasks</span>
      <div className="flex-1 hidden sm:block"></div>
      {onNewTask && (
        <Button onClick={onNewTask} className="bg-[#FF6B35] hover:bg-[#e55a2b] text-white" data-testid="rt-new-task-btn">
          + New Task
        </Button>
      )}
    </div>
  );
}

export function filterRestaurantTasks(tasks, { filterType, filterAssignee, selectedRestaurants }) {
  return tasks.filter(t => {
    if (filterType && t.task_type !== filterType) return false;
    if (filterAssignee && t.assignee_id !== filterAssignee) return false;
    if (selectedRestaurants?.length && !selectedRestaurants.includes(t.restaurant_id)) return false;
    return true;
  });
}

function dueUrgency(dueDate, status) {
  if (!dueDate || status === 'done') return null;
  const today = new Date();
  today.setHours(0, 0, 0, 0);
  const target = new Date(dueDate + 'T00:00:00');
  const days = Math.floor((target.getTime() - today.getTime()) / 86400000);
  if (days < 0) return { text: '#B91C1C', bg: '#FEE2E2', label: `Overdue ${Math.abs(days)}d` };
  if (days === 0) return { text: '#B91C1C', bg: '#FEE2E2', label: 'Today' };
  if (days < 3) return { text: '#92400E', bg: '#FEF3C7', label: `${days}d left` };
  return null;
}

export function RestaurantTaskCard({ task, onClick, onDragStart, showRestaurant = true }) {
  const type = RT_TYPE_MAP[task.task_type] || RT_TYPES[0];
  const age = getDaysInProgress(task);
  const urgency = dueUrgency(task.due_date, task.status);

  return (
    <div
      draggable
      onDragStart={(e) => onDragStart?.(e, task)}
      onClick={() => onClick(task)}
      className="border rounded-lg overflow-hidden cursor-pointer hover:shadow-md transition-shadow bg-white border-[#E0E0E0]"
      data-testid={`rt-card-${task.id}`}
    >
      <div className="w-full h-1" style={{ backgroundColor: type.color }} />
      <div className="p-3">
        <div className="flex items-center gap-2 mb-1">
          <span className="text-[10px] font-semibold uppercase tracking-wide" style={{ color: type.color }} data-testid={`rt-card-type-${task.id}`}>
            {type.label}
          </span>
        </div>
        <p className="text-sm font-medium text-[#111111] line-clamp-2">{task.title}</p>
        {task.description && (
          <p className="text-xs text-[#666666] mt-1 line-clamp-2 whitespace-pre-line">{task.description}</p>
        )}
        {showRestaurant && task.restaurant_name && (
          <p className="text-[11px] text-[#999999] mt-1 flex items-center gap-1">
            <Building size={11} />
            <span className="truncate">{task.restaurant_name}</span>
          </p>
        )}
        <div className="flex items-center gap-1.5 flex-wrap mt-2">
          {age && (
            <span
              className="inline-flex items-center gap-1 px-2 py-0.5 rounded text-[11px] font-medium"
              style={{ backgroundColor: age.frozen ? '#ECFDF5' : '#F5F5F5', color: age.frozen ? '#047857' : '#666666' }}
              title={age.frozen ? 'Days in progress (stopped in Done)' : 'Days in progress'}
              data-testid={`rt-card-age-${task.id}`}
            >
              <Clock size={11} /> {age.days}d
            </span>
          )}
          {task.due_date && (
            <span
              className="inline-flex items-center gap-1 px-2 py-0.5 rounded text-[11px] font-medium"
              style={{ backgroundColor: urgency?.bg || '#F5F5F5', color: urgency?.text || '#666666' }}
              data-testid={`rt-card-due-${task.id}`}
            >
              <CalendarClock size={11} /> {formatShortDate(task.due_date)}
              {urgency && <span className="opacity-80">• {urgency.label}</span>}
            </span>
          )}
        </div>
        <div className="flex items-center justify-between mt-2 pt-2 border-t border-[#E0E0E0]">
          {task.assignee_name ? (
            <div className="flex items-center gap-1.5">
              <div className="w-5 h-5 rounded-full bg-[#FFFF66] text-[#101010] flex items-center justify-center text-[10px] font-bold" title={task.assignee_name}>
                {task.assignee_initials}
              </div>
              <span className="text-xs text-[#666666] truncate max-w-[90px]">{task.assignee_name}</span>
            </div>
          ) : (
            <span className="text-xs text-[#999999]">Unassigned</span>
          )}
          <div className="flex items-center gap-2 text-[#666666]">
            {task.checklist?.length > 0 && (
              <span className="flex items-center gap-0.5 text-xs" data-testid={`rt-card-checklist-${task.id}`}>
                <CheckSquare size={12} />
                {task.checklist.filter(c => c.done).length}/{task.checklist.length}
              </span>
            )}
            {task.comment_count > 0 && (
              <span className="flex items-center gap-0.5 text-xs"><MessageSquare size={12} />{task.comment_count}</span>
            )}
            {task.link && (
              <a href={task.link} target="_blank" rel="noreferrer" onClick={(e) => e.stopPropagation()} title={task.link}>
                <LinkIcon size={12} className="hover:text-[#FF6B35]" />
              </a>
            )}
            {task.attachments?.length > 0 && (
              <span className="flex items-center gap-0.5 text-xs"><Paperclip size={12} />{task.attachments.length}</span>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}

export function RestaurantTaskBoard({ tasks, onTaskClick, onStatusChange, showRestaurant = true }) {
  const [draggedTask, setDraggedTask] = useState(null);
  const [collapsedCols, setCollapsedCols] = useState({});

  const isCollapsed = (status) => collapsedCols[status] ?? status === 'done';
  const toggle = (status) => setCollapsedCols(prev => ({ ...prev, [status]: !(prev[status] ?? status === 'done') }));

  const handleDrop = async (e, status) => {
    e.preventDefault();
    if (!draggedTask || draggedTask.status === status) { setDraggedTask(null); return; }
    await onStatusChange(draggedTask, status);
    setDraggedTask(null);
  };

  return (
    <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-3" data-testid="rt-board">
      {RT_STATUSES.map(col => {
        const colTasks = tasks.filter(t => t.status === col.value);
        const collapsed = isCollapsed(col.value);
        return (
          <div
            key={col.value}
            onDragOver={(e) => { e.preventDefault(); e.dataTransfer.dropEffect = 'move'; }}
            onDrop={(e) => handleDrop(e, col.value)}
            data-testid={`rt-column-${col.value}`}
          >
            <div className="bg-[#F5F5F5] rounded-t-lg px-2 py-2 flex items-center justify-between gap-1">
              <button
                onClick={() => toggle(col.value)}
                className="flex items-center gap-1 min-w-0 hover:text-[#FF6B35] transition-colors"
                data-testid={`rt-toggle-column-${col.value}`}
              >
                {collapsed ? <ChevronRight size={14} /> : <ChevronDown size={14} />}
                <span className="font-medium text-xs text-[#111111] uppercase tracking-wide truncate">{col.label}</span>
              </button>
              <Badge variant="secondary" className="text-xs">{colTasks.length}</Badge>
            </div>
            {collapsed ? (
              <div className="bg-[#F5F5F5] rounded-b-lg p-2 min-h-[44px] flex items-center justify-center text-[11px] text-[#999999]">
                {colTasks.length > 0 ? `${colTasks.length} hidden — click arrow to show` : 'Empty'}
              </div>
            ) : (
              <div className="bg-[#F5F5F5] rounded-b-lg p-2 min-h-[120px] space-y-2">
                {colTasks.map(task => (
                  <RestaurantTaskCard
                    key={task.id}
                    task={task}
                    showRestaurant={showRestaurant}
                    onClick={onTaskClick}
                    onDragStart={(e, t) => { setDraggedTask(t); e.dataTransfer.effectAllowed = 'move'; }}
                  />
                ))}
                {colTasks.length === 0 && (
                  <div className="flex items-center justify-center h-16 text-xs text-[#999999]">Empty</div>
                )}
              </div>
            )}
          </div>
        );
      })}
    </div>
  );
}

const WEEKDAYS = ['Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat', 'Sun'];

function buildMonthGrid(year, month) {
  const first = new Date(year, month, 1);
  const offset = (first.getDay() + 6) % 7; // Monday-first
  const start = new Date(year, month, 1 - offset);
  const cells = [];
  for (let i = 0; i < 42; i += 1) {
    const d = new Date(start.getFullYear(), start.getMonth(), start.getDate() + i);
    cells.push(d);
  }
  return cells;
}

const dateKey = (d) => `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;

export function RestaurantTaskCalendar({ tasks, onTaskClick }) {
  const [cursor, setCursor] = useState(() => {
    const now = new Date();
    return { year: now.getFullYear(), month: now.getMonth() };
  });

  const cells = useMemo(() => buildMonthGrid(cursor.year, cursor.month), [cursor]);
  const byDate = useMemo(() => {
    const map = {};
    tasks.forEach(t => {
      if (!t.due_date) return;
      map[t.due_date] = map[t.due_date] || [];
      map[t.due_date].push(t);
    });
    return map;
  }, [tasks]);

  const shift = (delta) => setCursor(c => {
    const d = new Date(c.year, c.month + delta, 1);
    return { year: d.getFullYear(), month: d.getMonth() };
  });

  const monthLabel = new Date(cursor.year, cursor.month, 1).toLocaleDateString('en-GB', { month: 'long', year: 'numeric' });
  const todayKey = dateKey(new Date());
  const undated = tasks.filter(t => !t.due_date);

  return (
    <div className="bg-white border border-[#E0E0E0] rounded-lg overflow-hidden" data-testid="rt-calendar">
      <div className="flex items-center justify-between px-4 py-3 border-b border-[#E0E0E0]">
        <h3 className="font-semibold text-[#111111]" data-testid="rt-calendar-month">{monthLabel}</h3>
        <div className="flex items-center gap-1">
          <Button variant="outline" size="icon" className="h-8 w-8" onClick={() => shift(-1)} data-testid="rt-calendar-prev"><ChevronLeft size={16} /></Button>
          <Button variant="outline" size="sm" className="h-8" onClick={() => { const n = new Date(); setCursor({ year: n.getFullYear(), month: n.getMonth() }); }} data-testid="rt-calendar-today">Today</Button>
          <Button variant="outline" size="icon" className="h-8 w-8" onClick={() => shift(1)} data-testid="rt-calendar-next"><ChevronRight size={16} /></Button>
        </div>
      </div>
      <div className="grid grid-cols-7 border-b border-[#E0E0E0] bg-[#FAFAFA]">
        {WEEKDAYS.map(d => (
          <div key={d} className="px-2 py-1.5 text-[11px] font-medium text-[#666666] uppercase tracking-wide text-center">{d}</div>
        ))}
      </div>
      <div className="grid grid-cols-7">
        {cells.map((d) => {
          const key = dateKey(d);
          const inMonth = d.getMonth() === cursor.month;
          const dayTasks = byDate[key] || [];
          return (
            <div
              key={key}
              className={`min-h-[92px] border-r border-b border-[#F0F0F0] p-1.5 ${inMonth ? 'bg-white' : 'bg-[#FAFAFA]'}`}
              data-testid={`rt-calendar-day-${key}`}
            >
              <div className="flex items-center justify-between mb-1">
                <span className={`text-[11px] font-medium ${key === todayKey ? 'bg-[#FF6B35] text-white rounded-full w-5 h-5 flex items-center justify-center' : inMonth ? 'text-[#111111]' : 'text-[#C0C0C0]'}`}>
                  {d.getDate()}
                </span>
                {dayTasks.length > 2 && <span className="text-[10px] text-[#999999]">{dayTasks.length}</span>}
              </div>
              <div className="space-y-1">
                {dayTasks.slice(0, 3).map(t => {
                  const type = RT_TYPE_MAP[t.task_type] || RT_TYPES[0];
                  return (
                    <button
                      key={t.id}
                      onClick={() => onTaskClick(t)}
                      className="w-full text-left px-1.5 py-1 rounded text-[10px] leading-tight truncate hover:opacity-80 transition-opacity"
                      style={{ backgroundColor: `${type.color}18`, color: type.color, textDecoration: t.status === 'done' ? 'line-through' : 'none' }}
                      title={`${t.restaurant_name || ''} — ${t.title}`}
                      data-testid={`rt-calendar-task-${t.id}`}
                    >
                      {t.title}
                    </button>
                  );
                })}
                {dayTasks.length > 3 && (
                  <span className="block text-[10px] text-[#999999] px-1.5">+{dayTasks.length - 3} more</span>
                )}
              </div>
            </div>
          );
        })}
      </div>
      {undated.length > 0 && (
        <div className="px-4 py-3 border-t border-[#E0E0E0] bg-[#FAFAFA]">
          <p className="text-xs text-[#666666] mb-2">No due date ({undated.length})</p>
          <div className="flex flex-wrap gap-1.5">
            {undated.map(t => {
              const type = RT_TYPE_MAP[t.task_type] || RT_TYPES[0];
              return (
                <button
                  key={t.id}
                  onClick={() => onTaskClick(t)}
                  className="px-2 py-1 rounded text-[11px] hover:opacity-80"
                  style={{ backgroundColor: `${type.color}18`, color: type.color }}
                  data-testid={`rt-calendar-undated-${t.id}`}
                >
                  {t.title}
                </button>
              );
            })}
          </div>
        </div>
      )}
    </div>
  );
}
